import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, Plus, Loader2, Sparkles, Send, X, Pencil, History, Check, Zap } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { timeAgo, initials, formatMeetingType, MEETING_TYPE_OPTIONS } from "@/lib/utils-ros";
import { AIInsightPanel } from "@/components/revenueos/AIInsightPanel";
import { useAuth } from "@/lib/auth";

/**
 * MeetingLogInline — reusable component to attach meetings to ANY entity
 * (company, signal, lead, opportunity, order). Shows existing meetings +
 * quick-log form + notes stream + AI curate.
 */
export function MeetingLogInline({ entityType, entityId, accent = "copper", readOnly: readOnlyProp }) {
  const { canWrite } = useAuth();
  const readOnly = readOnlyProp ?? !canWrite("customers");
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const navy = accent === "navy";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/meetings/for/${entityType}/${entityId}`);
      setMeetings(data);
    } catch {
      toast.error("Could not load meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [entityType, entityId]);

  return (
    <div className={navy ? "border border-[#C9DCE6] rounded-[22px] overflow-hidden bg-white shadow-[0_6px_18px_rgba(7,83,154,0.05)]" : "border border-black/10 rounded-md"} data-testid="meeting-log">
      <div className={`px-4 py-3 border-b flex items-center gap-2 ${navy ? "border-[#E1EBF0] bg-white" : "border-black/10 bg-black/[0.02]"}`}>
        <span className={navy ? "w-7 h-7 rounded-lg bg-[#E7F5FB] text-[#07539A] flex items-center justify-center" : "contents"}>
          <CalendarClock size={navy ? 13 : 14} className={navy ? "text-[#07539A]" : "text-[var(--copper)]"} />
        </span>
        <span className={`text-[12px] font-semibold ${navy ? "text-[#07539A]" : "uppercase tracking-widest"}`}>Meetings</span>
        <span data-testid="meeting-count" className={navy ? "min-w-6 h-6 px-2 rounded-full bg-[#E7F5FB] text-[#07539A] text-[10px] font-semibold flex items-center justify-center" : "text-[11px] font-mono text-[var(--text-dim)]"}>{meetings.length}</span>
        {!readOnly && (
        <button onClick={() => setLogOpen(true)} data-testid="log-meeting-btn"
                className={`ml-auto text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1 font-medium ${navy ? "bg-[#E7F5FB] border border-[#C9DCE6] !text-[#07539A] hover:bg-[#D8EEF7]" : "bg-[var(--copper)]/10 text-[var(--copper)] hover:bg-[var(--copper)]/20"}`}>
          <Plus size={11} /> Log meeting
        </button>
        )}
      </div>
      {loading ? (
        <div className="p-4 text-[12px] text-[var(--text-muted)] min-h-[3.5rem]">Loading…</div>
      ) : meetings.length === 0 ? (
        <div className="p-4 text-[12px] text-[var(--text-muted)] text-center">No meetings yet. Click <em>Log meeting</em> above.</div>
      ) : (
        <div className={`divide-y max-h-[400px] overflow-y-auto ${navy ? "divide-[#E1EBF0]" : "divide-black/10"}`}>
          {meetings.map(m => <MeetingItem key={m.id} m={m} onChange={load} navy={navy} readOnly={readOnly} />)}
        </div>
      )}
      {logOpen && <QuickLogModal entityType={entityType} entityId={entityId} onClose={() => { setLogOpen(false); load(); }} />}
    </div>
  );
}

function MeetingItem({ m, onChange, navy = false, readOnly = false }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [curating, setCurating] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(m.title);
  const [savingTitle, setSavingTitle] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);

  async function addNote() {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/meetings/${m.id}/notes`, { content: note });
      setNote("");
      toast.success("Note added");
      onChange?.();
    } catch {
      toast.error("Could not add note");
    } finally { setPosting(false); }
  }

  async function saveTitle() {
    if (!titleDraft.trim() || titleDraft === m.title) { setEditTitle(false); return; }
    setSavingTitle(true);
    try {
      await api.patch(`/meetings/${m.id}`, { title: titleDraft.trim() });
      toast.success("Meeting updated · version " + ((m.version || 1) + 1));
      setEditTitle(false);
      onChange?.();
    } catch (error) { toast.error(error.response?.data?.detail || "Save failed"); }
    finally { setSavingTitle(false); }
  }

  async function loadVersions() {
    try {
      const { data } = await api.get(`/meetings/${m.id}/versions`);
      setVersions(data);
      setShowVersions(true);
    } catch {
      toast.error("Could not load version history");
    }
  }

  async function process() {
    setCurating(true);
    try {
      const { data } = await api.post(`/meetings/${m.id}/process`);
      const n = data.processing?.tasks_created || 0;
      toast.success(`Meeting processed · ${n} task${n !== 1 ? "s" : ""} created`);
      onChange?.();
    } catch { toast.error("Processing failed"); }
    finally { setCurating(false); }
  }

  const versionBadge = (m.version || 1) > 1 ? `v${m.version}` : null;

  return (
    <div className="p-3 group" data-testid={`meeting-item-${m.id}`}>
      <div className="flex items-start gap-2">
        <div onClick={() => setExpanded(!expanded)} role="button" tabIndex={0}
             onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setExpanded(!expanded)}
             className="flex-1 text-left min-w-0 cursor-pointer">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest font-mono ${navy ? "text-[#07539A]" : "text-[var(--copper)]"}`}>{formatMeetingType(m.meeting_type)}</span>
            <span className="text-[11px] text-[var(--text-dim)]">·</span>
            <span className="text-[11px] text-[var(--text-dim)]">{timeAgo(m.scheduled_at)}</span>
            <span className="text-[11px] text-[var(--text-dim)]">·</span>
            <span className="text-[11px] text-[var(--text-dim)]">{(m.notes || []).length} note(s)</span>
            {m.ai_processed_at && (
              <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[var(--success)]/10 text-[var(--success)]">
                <Zap size={9} /> Processed
              </span>
            )}
            {versionBadge && (
              <button onClick={(e) => { e.stopPropagation(); loadVersions(); }} data-testid={`versions-${m.id}`}
                      className="text-[9px] px-1.5 py-0.5 rounded-sm bg-black/[0.05] text-[var(--text-muted)] hover:text-[var(--saarv-blue)] flex items-center gap-1">
                <History size={9} /> {versionBadge}
              </button>
            )}
          </div>
          {editTitle ? (
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <input value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && saveTitle()}
                     data-testid={`title-edit-${m.id}`}
                     className={`flex-1 h-8 px-2 rounded-md bg-white border outline-none text-[13px] ${navy ? "border-[#07539A]/40" : "border-[var(--copper)]/40"}`} autoFocus />
              <button onClick={saveTitle} disabled={savingTitle} data-testid={`title-save-${m.id}`} className={`${navy ? "text-[#07539A]" : "text-[var(--copper)]"} h-8 px-1.5`}>
                {savingTitle ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              </button>
              <button onClick={() => { setEditTitle(false); setTitleDraft(m.title); }} className="text-[var(--text-dim)] h-8 px-1.5"><X size={12} /></button>
            </div>
          ) : (
            <div className="text-[13px] flex items-center gap-1.5">
              {m.title}
              {!readOnly && (
              <button onClick={(e) => { e.stopPropagation(); setEditTitle(true); }}
                      data-testid={`title-edit-btn-${m.id}`}
                      className={`opacity-0 group-hover:opacity-100 text-[var(--text-dim)] ${navy ? "hover:text-[#07539A]" : "hover:text-[var(--copper)]"}`}
                      title="Edit title">
                <Pencil size={10} />
              </button>
              )}
            </div>
          )}
          {m.ai_summary && !expanded && <div className="text-[11px] text-[var(--text-muted)] mt-1 line-clamp-2">{m.ai_summary}</div>}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 space-y-3">
          {(m.notes || []).length > 0 && (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {m.notes.map(n => (
                <div key={n.id} className="flex items-start gap-2" data-testid={`note-${n.id}`}>
                  <div className="w-6 h-6 rounded-sm bg-black/[0.05] flex items-center justify-center text-[9px] font-mono shrink-0">{initials(n.author_name || "?")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-[var(--text-dim)]">{n.author_name} · {timeAgo(n.created_at)}</div>
                    <div className="text-[12px] whitespace-pre-wrap">{n.content}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!readOnly && (
          <div className="flex items-center gap-2">
            <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote()}
                   placeholder="Add your meeting notes…" data-testid={`add-note-${m.id}`}
                   className={`flex-1 h-8 px-2 rounded-md bg-white border border-black/10 outline-none text-[12px] ${navy ? "focus:border-[#07539A]" : "focus:border-[var(--copper)]"}`} />
            <button onClick={addNote} disabled={posting || !note.trim()} data-testid={`send-note-${m.id}`}
                    className={`${navy ? "text-[#07539A]" : "text-[var(--copper)]"} disabled:opacity-40 h-8 px-2`}>
              {posting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            </button>
            <button onClick={process} disabled={curating || (m.notes || []).length === 0} data-testid={`process-${m.id}`}
                    title="AI process → summary + action items + tasks + timeline"
                    className={`h-8 px-2 rounded-md !text-white text-[11px] disabled:opacity-40 flex items-center gap-1 ${navy ? "bg-[#07539A]" : "bg-[var(--copper)]"}`}>
              {curating ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
              Process
            </button>
          </div>
          )}
          {(curating || m.ai_summary) && (
            <div data-testid={`meeting-ai-insight-${m.id}`}>
              <AIInsightPanel
                title="AI curated summary"
                insight={m.ai_summary ? [
                  m.ai_summary,
                  ...(m.ai_action_items?.length
                    ? ["", "Action items:", ...m.ai_action_items.map((a) => `→ ${typeof a === "string" ? a : a?.action || a?.title || String(a)}`)]
                    : []),
                ].join("\n") : null}
                loading={curating}
                alwaysShow={curating}
                anchorId={`meeting-ai-${m.id}`}
                emptyText="Processing notes…"
                accent={navy ? "navy" : "copper"}
              />
            </div>
          )}
          {showVersions && (
            <div className="border-t border-black/10 pt-2" data-testid={`versions-list-${m.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] flex items-center gap-1"><History size={10} /> Version history</div>
                <button onClick={() => setShowVersions(false)} className="text-[var(--text-dim)] hover:text-[var(--text)]"><X size={10} /></button>
              </div>
              {versions.length === 0 ? (
                <div className="text-[11px] text-[var(--text-muted)]">Only the current version exists.</div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.id} className="flex items-center gap-2 text-[11px] py-1 px-2 bg-black/[0.02] rounded-sm">
                      <span className={`font-mono w-8 ${navy ? "text-[#07539A]" : "text-[var(--copper)]"}`}>v{v.version}</span>
                      <span className="text-[var(--text-muted)] flex-1 truncate">
                        by {v.changed_by_name} · {timeAgo(v.changed_at)}
                      </span>
                      <span className="text-[9px] text-[var(--text-dim)] uppercase tracking-wider">
                        {(v.changed_fields || []).slice(0, 2).join(", ")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuickLogModal({ entityType, entityId, onClose }) {
  const [form, setForm] = useState({ title: "", meeting_type: "teams", participants: "", notes: "" });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim() || !form.notes.trim()) return toast.error("Title and notes required");
    setSaving(true);
    try {
      await api.post("/meetings/quick-log", {
        title: form.title,
        entity_type: entityType,
        entity_id: entityId,
        meeting_type: form.meeting_type,
        participants: form.participants ? form.participants.split(",").map(s => s.trim()) : [],
        notes: form.notes,
      });
      toast.success("Meeting logged");
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="quick-log-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><CalendarClock size={15} /></div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Log a meeting</div>
          <button onClick={onClose} data-testid="quick-log-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]"><X size={15} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] text-[#455A64] font-semibold">Title *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="ql-title"
                   className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] text-[#455A64] font-semibold">Type</label>
              <select value={form.meeting_type} onChange={e => setForm({ ...form, meeting_type: e.target.value })} data-testid="ql-type"
                      className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]">
                {MEETING_TYPE_OPTIONS.map(t => <option key={t} value={t}>{formatMeetingType(t)}</option>)}
              </select>
            </div>
            <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
              <label className="text-[11px] text-[#455A64] font-semibold">Participants</label>
              <input value={form.participants} onChange={e => setForm({ ...form, participants: e.target.value })} data-testid="ql-participants"
                     placeholder="Comma-separated names"
                     className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]" />
            </div>
          </div>
          <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
            <label className="text-[11px] text-[#455A64] font-semibold">Notes *</label>
            <textarea rows={5} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="ql-notes"
                      placeholder="What was discussed? Who committed to what? Any red flags?"
                      className="w-full mt-1 px-3 py-2 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px] resize-none" />
          </div>
          <button onClick={save} disabled={saving} data-testid="ql-save"
                  className="w-full h-11 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Log meeting
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
